import { useEffect, useRef, useState } from "react";
import inputStyles from "../../../styles/UI/inputFields.module.css";
import ColouredRoundWrapper from "../wrappers/ColouredRoundWrapper";
import dateUtil from "../../../utils/dateUtil";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";
import DateCalendarComponent from "./DateCalendar";
import labels from "../../../utils/labels";

const DatePicker = ({
  id,
  value,
  label,
  placeholder,
  onDateChange,
  disabled,
  classes,
  min,
  max,
  onClickOverride,
}: {
  id: string;
  value: string;
  label?: string;
  placeholder?: string;
  onDateChange: (isoDate: string) => void;
  disabled?: boolean;
  classes?: string;
  min?: string;
  max?: string;
  onClickOverride?: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const suppressNextOpenRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const labelId = `${id}-label`;
  const popupId = `${id}-calendar`;

  useEffect(() => {
    if (!isOpen) return;
    const dayCell =
      popupRef.current?.querySelector<HTMLElement>(
        ".MuiPickersDay-root.Mui-selected",
      ) ??
      popupRef.current?.querySelector<HTMLElement>(".MuiPickersDay-today") ??
      popupRef.current?.querySelector<HTMLElement>(
        ".MuiPickersDay-root:not(.Mui-disabled)",
      );
    dayCell?.focus();
  }, [isOpen]);

  const inputValue = value
    ? dateUtil.getCalendarValueString(new Date(value))
    : "";

  const displayText = inputValue
    ? dateUtil.getCalendarDate(new Date(inputValue + "T12:00:00"))
    : undefined;

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: PointerEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside);
  }, [isOpen]);

  const handleMouseDown = () => {
    if (isOpen) suppressNextOpenRef.current = true;
  };

  const handleClick = () => {
    if (onClickOverride) {
      onClickOverride();
      return;
    }
    if (disabled) return;
    if (suppressNextOpenRef.current) {
      suppressNextOpenRef.current = false;
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    } else if (e.key === "Escape" && isOpen) {
      setIsOpen(false);
    }
  };

  const handleDateChange = (isoDate: string) => {
    onDateChange(isoDate);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handlePopupKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div
      id={id}
      ref={wrapperRef}
      className={`flex gap-[var(--spacing-xs)] flex-col relative ${classes ?? ""}`}
    >
      {label && (
        <span id={labelId} className={`pl-2 ${inputStyles.label}`}>
          {label}
        </span>
      )}
      <div
        ref={triggerRef}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={popupId}
        aria-disabled={disabled}
        aria-labelledby={label ? labelId : undefined}
        className="rounded-[var(--border-radius-minimal)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--col-border-focus)] focus-visible:outline-offset-1"
      >
        <ColouredRoundWrapper
          bgColor=""
          padding
          width="100%"
          borderColor="var(--col-border-primary)"
          classes={`flex items-center transition-colors ${
            disabled
              ? "bg-[var(--col-surface-secondary)]"
              : "bg-white hover:bg-[var(--col-surface-tertiary)] active:bg-[var(--col-graph-first)] cursor-pointer"
          }`}
        >
          <span
            className={`flex-1 min-w-0 [font:var(--font-bodyStd)] truncate pointer-events-none ${
              displayText
                ? "text-[var(--col-text-primary)]"
                : "text-[var(--col-text-secondary)]"
            }`}
          >
            {displayText ?? placeholder}
          </span>
          <CalendarIcon width={16} height={16} className="shrink-0" />
        </ColouredRoundWrapper>
      </div>
      {isOpen && !disabled && (
        <div
          ref={popupRef}
          onKeyDown={handlePopupKeyDown}
          id={popupId}
          role="dialog"
          aria-label={label ?? labels.datePicker}
          className="absolute top-full left-0 z-[1000]"
          style={{
            marginTop: "var(--spacing-xs)",
            backgroundColor: "white",
            borderRadius: "var(--border-radius-minimal)",
            boxShadow: "var(--boxshadow-light)",
            border: "1px solid var(--col-border-primary)",
            overflow: "hidden",
          }}
        >
          <DateCalendarComponent
            value={value}
            onChange={handleDateChange}
            min={min}
            max={max}
          />
        </div>
      )}
    </div>
  );
};

export default DatePicker;
